import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQPage = () => {
    const [openIndex, setOpenIndex] = useState(null);

    const faqs = [
        {
            question: "How does PeerChargeEV work?",
            answer: "PeerChargeEV connects EV owners with private charger hosts. Users can find and book nearby charging stations through our app, while hosts can list their chargers and earn money by sharing them. The platform handles scheduling, payments, and communication between parties."
        },
        {
            question: "How do I list my charger?",
            answer: (
                <>
                    To list your charger, download our app, create an account, and tap 'List a Charger'. You'll need to provide details about your charger type, location, availability, and pricing. Once verified, your charger will be visible to potential users in your area.
                    <br /><br />
                    <a href="https://youtu.be/gCWxZ6w3q9Q?list=PLk1m7XSACSNn2dFmgP0L3KnZlznhYFOum" target="_blank" rel="noopener noreferrer" className="text-[#4ade80] hover:underline">
                        Watch our video tutorial on how to add a new charger.
                    </a>
                </>
            )
        },
        {
            question: "How are payments handled?",
            answer: "Payments are settled directly between the driver and the host. We are not involved in the actual payment transfer for the charging session. We only deduct a flat ₹1 service charge per booking from the user, ensuring hosts receive 100% of their agreed earnings directly from the customer."
        },
        {
            question: "Is my charging station insured?",
            answer: "While PeerChargeEV doesn't provide direct insurance, we recommend hosts to have appropriate home insurance coverage. We do implement strict user verification and safety protocols to minimize risks."
        },
        {
            question: "How are prices set?",
            answer: "Hosts have the freedom to set their own charging rates. We recommend considering local electricity costs, market rates, and competition. The app shows users all costs upfront, including any service fees."
        },
        {
            question: "What happens if something goes wrong during charging?",
            answer: "Our app provides 24/7 support. In case of any issues, users and hosts can contact us immediately through the app. We have a dispute resolution process and maintain security deposits to handle potential problems."
        },
        {
            question: "How do I find available chargers?",
            answer: "Open the PeerChargeEV app and use our map interface to find nearby chargers. You can filter by charger type, availability, price range, and ratings. Each listing shows detailed information about the charger and host."
        },
        {
            question: "What types of chargers are supported?",
            answer: "We support various charging standards including Type 1, Type 2, CCS, CHAdeMO, and standard household outlets. The app clearly shows the connector types available at each location to ensure compatibility with your vehicle."
        }
    ];

    const FAQItem = ({ question, answer, isOpen, onClick }) => (
        <div className="border-b border-gray-700 last:border-b-0 py-4">
            <button
                className="w-full flex justify-between items-center text-left focus:outline-none"
                onClick={onClick}
            >
                <h3 className="text-lg font-medium text-white">{question}</h3>
                {isOpen ? (
                    <ChevronUp className="text-[#4ade80] w-6 h-6" />
                ) : (
                    <ChevronDown className="text-[#4ade80] w-6 h-6" />
                )}
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-6' : 'max-h-0'}`}>
                <p className="text-gray-400">{answer}</p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-900 py-20">
            <Helmet>
                <title>FAQ | PeerChargeEV - Frequently Asked Questions</title>
                <meta name="description" content="Find answers to common questions about PeerChargeEV, how P2P charging works, host earnings, and more." />
            </Helmet>
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto">
                    <h1 className="text-4xl font-bold text-center text-white mb-4">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-gray-400 text-center mb-12">
                        Everything you need to know about PeerChargeEV
                    </p>
                    <div className="bg-gray-800 rounded-2xl p-6 shadow-xl">
                        {faqs.map((faq, index) => (
                            <FAQItem
                                key={index}
                                question={faq.question}
                                answer={faq.answer}
                                isOpen={openIndex === index}
                                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                            />
                        ))}
                    </div>
                    <div className="text-center mt-12">
                        <p className="text-gray-400">
                            Still have questions?{' '}
                            <a href="mailto:peerchargeev@gmail.com" className="text-[#4ade80] hover:underline">
                                Contact our support team
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FAQPage;